// src/pages/admin/AdminInvestmentSubscriptions.jsx
import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "../../services/api";

const STATUS_OPTIONS = [
  { value: "", label: "All statuses" },
  { value: "active", label: "Active" },
  { value: "due_soon", label: "Due soon (<= 7 days)" },
  { value: "due_today", label: "Due today" },
  { value: "overdue", label: "Overdue" },
  { value: "matured", label: "Matured" },
  { value: "closed", label: "Closed" },
];

const PAGE_SIZE_OPTIONS = [10, 25, 50, 100];

const formatCurrency = (value) =>
  Number(value || 0).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const formatDate = (value) => (value ? new Date(value).toLocaleDateString() : "--");

const humanizeStatus = (value) =>
  (value || "")
    .split("_")
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ") || "Unknown";

const AdminInvestmentSubscriptions = () => {
  const navigate = useNavigate();
  const [filters, setFilters] = useState({
    search: "",
    status: "",
    page: 1,
    pageSize: 25,
  });
  const [searchInput, setSearchInput] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [response, setResponse] = useState({
    items: [],
    page: 1,
    total: 0,
    summary: {
      status_breakdown: {},
      outstanding_principal: 0,
      accrued_interest: 0,
    },
  });

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      setError("");
      try {
        const data = await api.adminListInvestmentSubscriptions({
          search: filters.search || undefined,
          status: filters.status || undefined,
          page: filters.page,
          page_size: filters.pageSize,
        });
        if (cancelled) return;
        setResponse({
          items: Array.isArray(data?.items) ? data.items : [],
          page: data?.page || filters.page,
          total: data?.total || 0,
          summary: {
            status_breakdown: data?.summary?.status_breakdown || {},
            outstanding_principal: data?.summary?.outstanding_principal || 0,
            accrued_interest: data?.summary?.accrued_interest || 0,
          },
        });
      } catch (err) {
        if (!cancelled) {
          setError(err?.message || "Failed to load subscribed investments.");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [filters]);

  useEffect(() => {
    setSearchInput(filters.search || "");
  }, [filters.search]);

  const totalPages = useMemo(() => {
    const total = Number(response.total || 0);
    const size = Number(filters.pageSize || 1);
    return Math.max(1, Math.ceil(total / size));
  }, [response.total, filters.pageSize]);

  const statusChips = useMemo(() => {
    const breakdown = response.summary.status_breakdown || {};
    return Object.entries(breakdown)
      .sort((a, b) => (b[1] || 0) - (a[1] || 0))
      .map(([key, value]) => ({
        key,
        label: humanizeStatus(key),
        count: value,
      }));
  }, [response.summary.status_breakdown]);

  const handleSubmit = (event) => {
    event.preventDefault();
    setFilters((prev) => ({
      ...prev,
      search: searchInput.trim(),
      page: 1,
    }));
  };

  const handleStatusChange = (event) => {
    setFilters((prev) => ({
      ...prev,
      status: event.target.value,
      page: 1,
    }));
  };

  const handlePageChange = (direction) => {
    setFilters((prev) => {
      const nextPage = Math.min(
        totalPages,
        Math.max(1, prev.page + direction)
      );
      return nextPage === prev.page ? prev : { ...prev, page: nextPage };
    });
  };

  const handlePageSizeChange = (event) => {
    const nextSize = parseInt(event.target.value, 10) || 25;
    setFilters((prev) => ({
      ...prev,
      pageSize: nextSize,
      page: 1,
    }));
  };

  return (
    <div className="space-y-6">
      <header className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900">Subscribed investments</h1>
          <p className="text-sm text-slate-500">
            Track user investments, outstanding balances, and upcoming cashflows.
          </p>
        </div>
      </header>

      <section className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
        <form
          className="grid gap-4 md:grid-cols-[2fr_1fr_1fr_auto]"
          onSubmit={handleSubmit}
        >
          <div>
            <label className="block text-xs font-semibold uppercase text-slate-500">
              Search
            </label>
            <input
              type="text"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              placeholder="Search by investment, user, or phone number"
              className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-emerald-500 focus:outline-none"
            />
          </div>
          <div>
            <label className="block text-xs font-semibold uppercase text-slate-500">
              Status
            </label>
            <select
              value={filters.status}
              onChange={handleStatusChange}
              className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-emerald-500 focus:outline-none"
            >
              {STATUS_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs font-semibold uppercase text-slate-500">
              Page size
            </label>
            <select
              value={filters.pageSize}
              onChange={handlePageSizeChange}
              className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-emerald-500 focus:outline-none"
            >
              {PAGE_SIZE_OPTIONS.map((size) => (
                <option key={size} value={size}>
                  {size} per page
                </option>
              ))}
            </select>
          </div>
          <div className="flex items-end">
            <button
              type="submit"
              className="w-full rounded-lg bg-emerald-600 px-4 py-2 text-sm font-semibold text-white shadow hover:bg-emerald-700 focus:outline-none focus:ring-2 focus:ring-emerald-500"
            >
              Apply filters
            </button>
          </div>
        </form>
      </section>

      <section className="grid gap-4 md:grid-cols-3">
        <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
          <p className="text-xs font-semibold uppercase text-slate-500">
            Total subscribed investments
          </p>
          <p className="mt-2 text-2xl font-semibold text-slate-900">
            {response.total.toLocaleString()}
          </p>
          <div className="mt-3 flex flex-wrap gap-2">
            {statusChips.length > 0 ? (
              statusChips.map((chip) => (
                <span
                  key={chip.key}
                  className="inline-flex items-center rounded-full bg-slate-100 px-2.5 py-1 text-xs text-slate-600"
                >
                  {chip.label}:{" "}
                  <span className="ml-1 font-semibold text-slate-900">
                    {chip.count}
                  </span>
                </span>
              ))
            ) : (
              <span className="text-xs text-slate-400">
                No breakdown available yet.
              </span>
            )}
          </div>
        </div>
        <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
          <p className="text-xs font-semibold uppercase text-slate-500">
            Outstanding principal
          </p>
          <p className="mt-2 text-2xl font-semibold text-slate-900">
            ₦{formatCurrency(response.summary.outstanding_principal)}
          </p>
          <p className="mt-1 text-xs text-slate-500">
            Aggregated across all active subscriptions.
          </p>
        </div>
        <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
          <p className="text-xs font-semibold uppercase text-slate-500">
            Accrued interest
          </p>
          <p className="mt-2 text-2xl font-semibold text-slate-900">
            ₦{formatCurrency(response.summary.accrued_interest)}
          </p>
          <p className="mt-1 text-xs text-slate-500">
            Expected payouts excluding upcoming capital.
          </p>
        </div>
      </section>

      <section className="rounded-xl border border-slate-200 bg-white shadow-sm">
        <div className="flex items-center justify-between border-b border-slate-200 px-4 py-3">
          <p className="text-sm font-semibold text-slate-700">
            {response.total.toLocaleString()} subscription
            {response.total === 1 ? "" : "s"} found
          </p>
          <div className="flex items-center gap-3 text-xs text-slate-500">
            <span>
              Page {response.page} of {totalPages}
            </span>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => handlePageChange(-1)}
                disabled={filters.page <= 1 || loading}
                className="rounded border border-slate-200 px-2 py-1 text-sm text-slate-600 disabled:cursor-not-allowed disabled:opacity-60"
              >
                Prev
              </button>
              <button
                type="button"
                onClick={() => handlePageChange(1)}
                disabled={filters.page >= totalPages || loading}
                className="rounded border border-slate-200 px-2 py-1 text-sm text-slate-600 disabled:cursor-not-allowed disabled:opacity-60"
              >
                Next
              </button>
            </div>
          </div>
        </div>

        {error && (
          <div className="border-b border-red-100 bg-red-50 px-4 py-3 text-sm text-red-600">
            {error}
          </div>
        )}

        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-slate-200 text-sm">
            <thead className="bg-slate-50 text-left text-xs font-semibold uppercase text-slate-500">
              <tr>
                <th className="px-4 py-2">Investment</th>
                <th className="px-4 py-2">User</th>
                <th className="px-4 py-2 text-right">Principal</th>
                <th className="px-4 py-2 text-right">Outstanding</th>
                <th className="px-4 py-2 text-right">Accrued interest</th>
                <th className="px-4 py-2">Status</th>
                <th className="px-4 py-2">Next due</th>
                <th className="px-4 py-2"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {loading ? (
                <tr>
                  <td colSpan={8} className="px-4 py-6">
                    <div className="flex items-center justify-center gap-2 text-sm text-slate-500">
                      <span className="h-4 w-4 animate-spin rounded-full border-2 border-slate-300 border-t-emerald-500" />
                      Loading subscriptions…
                    </div>
                  </td>
                </tr>
              ) : response.items.length === 0 ? (
                <tr>
                  <td colSpan={8} className="px-4 py-6 text-center text-sm text-slate-500">
                    No subscribed investments match your filters.
                  </td>
                </tr>
              ) : (
                response.items.map((item) => {
                  const detailPath = `/admin/investments/subscriptions/${item.id}`;
                  return (
                    <tr
                      key={item.id}
                      role="link"
                      tabIndex={0}
                      onClick={() => navigate(detailPath)}
                      onKeyDown={(event) => {
                        if (event.key === "Enter" || event.key === " ") {
                          event.preventDefault();
                          navigate(detailPath);
                        }
                      }}
                      className="cursor-pointer transition hover:bg-emerald-50 focus:bg-emerald-50 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-emerald-500"
                    >
                    <td className="px-4 py-3">
                      <Link
                        to={detailPath}
                        className="font-medium text-emerald-700 hover:text-emerald-800 hover:underline"
                      >
                        {item.name}
                      </Link>
                      <div className="text-xs text-slate-500">
                        {item.opportunity_name || item.opportunity_type || "Custom"}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="font-medium text-slate-900">
                        {item.user?.full_name || item.user?.username || "Unknown user"}
                      </div>
                      <div className="text-xs text-slate-500">{item.user?.role || "user"}</div>
                    </td>
                    <td className="px-4 py-3 text-right font-medium text-slate-900">
                      ₦{formatCurrency(item.amount)}
                    </td>
                    <td className="px-4 py-3 text-right text-slate-700">
                      ₦{formatCurrency(item.outstanding_principal)}
                    </td>
                    <td className="px-4 py-3 text-right text-slate-700">
                      ₦{formatCurrency(item.accrued_interest)}
                    </td>
                    <td className="px-4 py-3">
                      <span className="inline-flex rounded-full bg-slate-100 px-2 py-1 text-xs font-medium text-slate-700">
                        {humanizeStatus(item.status)}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      {item.next_cashflow?.due_date ? (
                        <div className="flex flex-col text-xs text-slate-600">
                          <span className="font-medium text-slate-900">
                            {formatDate(item.next_cashflow.due_date)}
                          </span>
                          <span>{humanizeStatus(item.next_cashflow.cashflow_type)}</span>
                          {typeof item.next_due_in_days === "number" && (
                            <span>
                              in {item.next_due_in_days} day{item.next_due_in_days === 1 ? "" : "s"}
                            </span>
                          )}
                        </div>
                      ) : (
                        <span className="text-xs text-slate-400">No upcoming cashflow</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <Link
                        to={detailPath}
                        className="text-sm font-semibold text-emerald-600 hover:text-emerald-700"
                      >
                        View
                      </Link>
                    </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
};

export default AdminInvestmentSubscriptions;
